/**
 * Multiplayer Rummy Service
 *
 * Extends GenericMultiplayerService for online play.
 * Game type: 'rummy'
 */

import { WebSocketService } from '../../services/WebSocketService';
import {
  GenericMultiplayerService,
} from '../../services/multiplayer/GenericMultiplayerService';
import {
  RummyGameState,
  RummyAction,
  Card,
} from '../types';

export class MultiplayerRummyService extends GenericMultiplayerService<
  RummyGameState,
  RummyAction
> {
  private onCardsDealtCallback?: (hand: Card[]) => void;
  private onDiscardCallback?: (card: Card, playerId: string) => void;
  private onDrawCallback?: (playerId: string, source: 'stock' | 'discard') => void;
  private onDeclarationCallback?: (playerId: string, valid: boolean) => void;
  private onRoundCompleteCallback?: (winnerId: string, scores: Record<string, number>) => void;
  private onTurnChangeCallback?: (playerId: string) => void;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 3;
  private playerSeat: number | null = null;

  constructor(wsService: WebSocketService) {
    super(wsService, 'rummy');
  }

  protected setupMessageHandlers(): void {
    this.wsService.setCustomMessageHandler((message: any) => {
      const { action } = message;
      if (!action || !action.startsWith('rummy:')) return;
      const gameAction = action.replace('rummy:', '');

      switch (gameAction) {
        case 'roomCreated':
          this.roomCode = message.roomCode;
          this.playerRole = 'player1';
          this.playerSeat = message.seatIndex ?? 0;
          if (this.onGameStartedCallback) this.onGameStartedCallback();
          break;
        case 'roomJoined':
          this.roomCode = message.roomCode;
          this.playerRole = message.playerRole || 'player2';
          this.playerSeat = message.seatIndex ?? 1;
          if (this.onGameStartedCallback) this.onGameStartedCallback();
          break;
        case 'opponentJoined':
          if (this.onPlayerConnectedCallback) {
            this.onPlayerConnectedCallback(message.playerId || 'opponent');
          }
          if (this.onGameStartedCallback) {
            this.onGameStartedCallback();
          }
          break;
        case 'gameStarted':
          if (message.gameState) {
            this.gameState = message.gameState;
            if (this.onGameStateReceivedCallback) this.onGameStateReceivedCallback(message.gameState);
          }
          if (this.onGameStartedCallback) this.onGameStartedCallback();
          break;
        case 'cardsDealt':
          if (this.onCardsDealtCallback) this.onCardsDealtCallback(message.hand);
          break;
        case 'cardDiscarded':
          if (this.onDiscardCallback) this.onDiscardCallback(message.card, message.playerId);
          break;
        case 'cardDrawn':
          if (this.onDrawCallback) this.onDrawCallback(message.playerId, message.source);
          break;
        case 'declaration':
          if (this.onDeclarationCallback) this.onDeclarationCallback(message.playerId, message.valid);
          break;
        case 'roundComplete':
          if (this.onRoundCompleteCallback) this.onRoundCompleteCallback(message.winnerId, message.scores);
          break;
        case 'turnChange':
          if (this.onTurnChangeCallback) this.onTurnChangeCallback(message.playerId);
          break;
        case 'gameOver':
          if (this.onGameOverCallback) this.onGameOverCallback({ result: 'win', winner: message.winnerId, reason: 'game_complete' });
          break;
        case 'gameState':
          if (message.gameState) {
            this.gameState = message.gameState;
            this.reconnectAttempts = 0;
            if (this.onGameStateReceivedCallback) this.onGameStateReceivedCallback(message.gameState);
          }
          break;
        case 'opponentDisconnected':
          if (this.onPlayerDisconnectedCallback) this.onPlayerDisconnectedCallback(message.playerId);
          break;
        case 'opponentReconnected':
          if (this.onPlayerConnectedCallback) this.onPlayerConnectedCallback(message.playerId);
          break;
        case 'error':
          this.handleError(message.message || 'Unknown multiplayer error');
          break;
      }
    });
  }

  protected sendCreateRoomMessage(): void {
    this.wsService.send({ action: 'rummy:createRoom', playerId: this.playerId, gameType: this.gameType });
  }

  protected sendJoinRoomMessage(roomCode: string): void {
    this.wsService.send({ action: 'rummy:joinRoom', roomCode, playerId: this.playerId, gameType: this.gameType });
  }

  protected sendLeaveRoomMessage(): void {
    this.wsService.send({ action: 'rummy:resign', roomCode: this.roomCode, playerId: this.playerId });
  }

  protected getMaxPlayers(): number { return 6; }

  async sendMove(move: RummyAction): Promise<boolean> {
    try {
      this.wsService.send({ action: 'rummy:action', roomCode: this.roomCode, playerId: this.playerId, playerAction: move });
      return true;
    } catch (error) {
      this.handleError('Failed to send action', error);
      return false;
    }
  }

  isMyTurn(currentTurn: string): boolean {
    return this.playerId === currentTurn || this.playerRole === currentTurn;
  }

  async resign(): Promise<void> {
    this.wsService.send({ action: 'rummy:resign', roomCode: this.roomCode, playerId: this.playerId });
  }

  getPlayerSeat(): number | null { return this.playerSeat; }

  // Event callbacks
  onCardsDealt(cb: (hand: Card[]) => void): void { this.onCardsDealtCallback = cb; }
  onDiscard(cb: (card: Card, playerId: string) => void): void { this.onDiscardCallback = cb; }
  onDraw(cb: (playerId: string, source: 'stock' | 'discard') => void): void { this.onDrawCallback = cb; }
  onDeclaration(cb: (playerId: string, valid: boolean) => void): void { this.onDeclarationCallback = cb; }
  onRoundComplete(cb: (winnerId: string, scores: Record<string, number>) => void): void { this.onRoundCompleteCallback = cb; }
  onTurnChange(cb: (playerId: string) => void): void { this.onTurnChangeCallback = cb; }

  // Connection management
  handleDisconnect(): void {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const d = Math.pow(2, this.reconnectAttempts - 1) * 1000;
      setTimeout(() => this.reconnect(), d);
    }
  }

  reconnect(): void {
    if (this.roomCode) {
      this.wsService.send({ action: 'rummy:reconnect', roomCode: this.roomCode, playerId: this.playerId });
    }
  }

  getReconnectAttempts(): number { return this.reconnectAttempts; }
}
